import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Power, RefreshCw, Loader2, ShieldAlert, History, EyeOff,
  AlertTriangle, Clock, Wrench, Plug, Mail, MessageSquare, Users, Gift, Star,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { fmtDateTime } from "@/lib/datetime";
import WelcomeMessageEditor from "./WelcomeMessageEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  overdue_escalation: Clock,
  maintenance_reminders: Wrench,
  ghl_crm_sync: Plug,
  customer_booking_emails: Mail,
  owner_booking_alerts: MessageSquare,
  sms_notifications: MessageSquare,
  email_notifications: Mail,
  loyalty_referrals: Gift,
  review_requests: Star,
};

const DESCRIPTIONS = {
  overdue_escalation: "Late-fee reminders & recovery/repossession alerts for overdue rentals.",
  maintenance_reminders: "Owner alerts when a vehicle is due for oil changes or service.",
  ghl_crm_sync: "Pushes contacts & pipeline stage tags into GoHighLevel.",
  customer_booking_emails: "Booking-received confirmation emails to customers.",
  owner_booking_alerts: "SMS & email alerts to you when a new booking comes in.",
  sms_notifications: "Master channel for ALL outbound text messages.",
  email_notifications: "Master channel for ALL outbound emails.",
  loyalty_referrals: "Referral discounts & loyalty credits on new bookings.",
  review_requests: "Post-rental review request emails to customers.",
};

function fmtTs(ts) {
  if (!ts) return "—";
  try { return fmtDateTime(ts); } catch { return ts; }
}

export default function AutomationCenter({ token, onAuthError }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [modal, setModal] = useState(null); // { type:'toggle'|'master', key, label, turningOff }
  const [reason, setReason] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/automations`, auth);
      setState(data);
    } catch (e) {
      if (e?.response?.status === 401 && onAuthError) onAuthError();
      else toast.error("Failed to load automation settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const commitToggle = async (key, enabled, why) => {
    setSaving(key);
    try {
      await axios.patch(`${API}/automations/${key}`, { enabled, reason: why || "" }, auth);
      toast.success(`${enabled ? "Enabled" : "Disabled"} ${state.automations.find((a) => a.key === key)?.label}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update automation");
    } finally {
      setSaving("");
    }
  };

  const commitMaster = async (paused, why) => {
    setSaving("__master__");
    try {
      await axios.post(`${API}/automations/master`, { paused, reason: why || "" }, auth);
      toast.success(paused ? "All automations paused" : "Automations resumed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update master switch");
    } finally {
      setSaving("");
    }
  };

  const onToggle = (item, next) => {
    if (!next) {
      setReason("");
      setModal({ type: "toggle", key: item.key, label: item.label });
    } else {
      commitToggle(item.key, true, "");
    }
  };

  const onMaster = (nextPaused) => {
    if (nextPaused) {
      setReason("");
      setModal({ type: "master" });
    } else {
      commitMaster(false, "");
    }
  };

  const submitModal = () => {
    if (!reason.trim()) { toast.error("Please enter a reason"); return; }
    if (modal.type === "master") commitMaster(true, reason.trim());
    else commitToggle(modal.key, false, reason.trim());
    setModal(null);
  };

  if (loading && !state) {
    return <div className="flex items-center gap-2 text-white/60 py-10"><Loader2 className="animate-spin" size={18} /> Loading automation controls…</div>;
  }
  if (!state) return null;

  const paused = state.master_paused;

  return (
    <div className="space-y-6" data-testid="automation-center">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Automation Control Center</h2>
          <p className="text-white/50 text-sm">Turn automations on/off. When off, events are still logged — the message just isn't sent.</p>
        </div>
        <button data-testid="automation-refresh-btn" onClick={load}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/80 hover:text-white text-sm font-semibold">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Master switch */}
      <div data-testid="automation-master-card"
        className={`rounded-xl p-5 border flex items-center justify-between gap-4 ${paused ? "bg-red-500/10 border-red-500/40" : "bg-[#0C0C0C] border-white/10"}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className={`p-2.5 rounded-lg ${paused ? "bg-red-500/20 text-red-400" : "bg-[#F2B705]/15 text-[#F2B705]"}`}>
            <Power size={20} />
          </div>
          <div className="min-w-0">
            <div className="text-white font-semibold flex items-center gap-2">
              Master — Pause All Automations
              {paused && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40">PAUSED</span>}
            </div>
            <div className="text-white/50 text-sm">{paused ? "Every automation below is currently paused." : "Emergency kill switch — pauses every automation at once."}</div>
          </div>
        </div>
        <Switch data-testid="automation-master-switch" checked={paused} disabled={saving === "__master__"}
          onCheckedChange={onMaster} className="data-[state=checked]:bg-red-500" />
      </div>

      {paused && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/40">
          <ShieldAlert className="text-amber-400 mt-0.5 shrink-0" size={18} />
          <div className="text-sm text-amber-200">All automations are paused. Individual switches below are overridden until you resume.</div>
        </div>
      )}

      {/* Welcome message (editable template) */}
      <WelcomeMessageEditor token={token} onAuthError={onAuthError} />

      {/* Individual toggles */}
      <div className="grid md:grid-cols-2 gap-4">
        {state.automations.map((item) => {
          const Icon = ICONS[item.key] || Plug;
          const count = state.suppressed_counts_recent?.[item.key] || 0;
          return (
            <div key={item.key} data-testid={`automation-row-${item.key}`}
              className="rounded-xl bg-[#0C0C0C] border border-white/10 p-4 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`p-2 rounded-lg shrink-0 ${item.effective ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-white/40"}`}>
                  <Icon size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-white font-semibold flex items-center gap-2 flex-wrap">
                    {item.label}
                    {!item.enabled && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/50">OFF</span>}
                    {item.enabled && !item.effective && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300">paused by master</span>}
                  </div>
                  <div className="text-white/50 text-xs mt-0.5 leading-snug">{DESCRIPTIONS[item.key]}</div>
                  {count > 0 && (
                    <div className="text-[11px] text-amber-300/80 mt-1.5 flex items-center gap-1">
                      <EyeOff size={11} /> {count} suppressed event{count > 1 ? "s" : ""} logged (would have fired)
                    </div>
                  )}
                </div>
              </div>
              <Switch data-testid={`automation-switch-${item.key}`} checked={item.enabled} disabled={saving === item.key}
                onCheckedChange={(v) => onToggle(item, v)} className="data-[state=checked]:bg-emerald-500 mt-0.5 shrink-0" />
            </div>
          );
        })}
      </div>

      {/* Rental Care & Reviews tag scheduler (read-only) */}
      <RentalCareStatus token={token} />

      {/* Suppressed events */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><EyeOff size={16} className="text-amber-400" /> Suppressed Events — What Would Have Fired</h3>
        {(!state.suppressed_events || state.suppressed_events.length === 0) ? (
          <p className="text-white/40 text-sm">Nothing suppressed. Everything is firing normally.</p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="automation-suppressed-list">
            {state.suppressed_events.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2.5 text-sm gap-3">
                <div className="min-w-0">
                  <div className="text-white/80 truncate">{e.label} <span className="text-white/30">· {e.channel}</span></div>
                  <div className="text-white/40 text-xs truncate">{e.detail} → {e.to}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">blocked by {e.blocked_by}</span>
                  <span className="text-white/40 text-xs whitespace-nowrap">{fmtTs(e.ts)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit trail */}
      <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5">
        <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><History size={16} className="text-[#F2B705]" /> Change Log</h3>
        {(!state.audit || state.audit.length === 0) ? (
          <p className="text-white/40 text-sm">No changes yet.</p>
        ) : (
          <div className="divide-y divide-white/5" data-testid="automation-audit-list">
            {state.audit.map((a) => (
              <div key={a.id} className="py-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-white/80 truncate">
                    <span className={`font-semibold ${["disabled", "paused"].includes(a.action) ? "text-red-400" : "text-emerald-400"}`}>{a.action.toUpperCase()}</span>
                    {" · "}{a.label}
                  </div>
                  <span className="text-white/40 text-xs whitespace-nowrap shrink-0">{fmtTs(a.ts)}</span>
                </div>
                <div className="text-white/40 text-xs mt-0.5">by {a.changed_by}{a.reason ? ` — "${a.reason}"` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reason modal */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-white" data-testid="automation-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={18} />
              {modal?.type === "master" ? "Pause ALL Automations" : `Turn Off — ${modal?.label}`}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/60 text-sm -mt-1">
            Add a reason so you (or your team) know why this was turned off. Events will still be logged, but no messages will be sent.
          </p>
          <Textarea data-testid="automation-reason-input" value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder='e.g. "Testing new workflow"' rows={3}
            className="bg-black border-white/15 text-white focus:border-[#F2B705]" />
          <DialogFooter>
            <button onClick={() => setModal(null)}
              className="px-4 py-2 rounded-md bg-[#0C0C0C] border border-white/10 text-white/70 text-sm font-semibold">Cancel</button>
            <button data-testid="automation-reason-confirm" onClick={submitModal}
              className="px-4 py-2 rounded-md bg-red-500 text-white text-sm font-semibold hover:bg-red-600">
              {modal?.type === "master" ? "Pause All" : "Turn Off"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RentalCareStatus({ token }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    axios.get(`${API}/automations/rental-care/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setS(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [token]);
  if (!s) return null;
  const c = s.counts || {};
  const Row = ({ r }) => (
    <div className="flex items-center justify-between py-2 text-xs gap-3">
      <span className="text-white/70 truncate">
        <b>{r.customer || r.booking_id}</b> · {r.vehicle || "—"} · <span className="text-white/40">{r.tag}</span>
      </span>
      <span className="flex items-center gap-2 shrink-0">
        <span className="text-white/40">{r.due_ct}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
          r.status === "applied" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
          : r.status === "scheduled" ? "bg-sky-500/15 text-sky-300 border-sky-500/30"
          : r.status === "failed" ? "bg-red-500/15 text-red-300 border-red-500/30"
          : "bg-white/10 text-white/50 border-white/15"}`}>{r.status}</span>
      </span>
    </div>
  );
  return (
    <div className="rounded-xl bg-[#0C0C0C] border border-white/10 p-5" data-testid="rental-care-status">
      <h3 className="text-white font-semibold mb-1">Rental Care & Reviews — GHL Tag Scheduler <span className="text-[10px] font-bold text-white/40 border border-white/15 rounded px-1.5 py-0.5 ml-1">READ-ONLY</span></h3>
      <p className="text-white/40 text-xs mb-3">
        Applies <b>rental-checkin-24h</b> (24h after handoff) and <b>rental-return-24h</b> (24h before scheduled return, Central Time) to the renter's GHL contact.
        The draft workflow owns all messages. Automation is {s.automation_on ? <span className="text-emerald-300 font-bold">ON</span> : <span className="text-amber-300 font-bold">OFF (default)</span>}.
      </p>
      <div className="flex flex-wrap gap-3 text-xs mb-2" data-testid="rental-care-counts">
        <span className="text-sky-300">{c.scheduled || 0} scheduled</span>
        <span className="text-emerald-300">{c.applied || 0} applied</span>
        <span className="text-white/50">{c.skipped || 0} skipped</span>
        <span className="text-white/50">{c.cancelled || 0} cancelled</span>
        <span className={c.failed ? "text-red-300 font-bold" : "text-white/50"}>{c.failed || 0} failed</span>
      </div>
      {(s.upcoming || []).length > 0 && (
        <div className="divide-y divide-white/5" data-testid="rental-care-upcoming">
          <div className="text-[10px] uppercase tracking-wide text-white/35 pt-1">Upcoming</div>
          {s.upcoming.map((r, i) => <Row key={`u${i}`} r={r} />)}
        </div>
      )}
      {(s.recent || []).length > 0 && (
        <div className="divide-y divide-white/5" data-testid="rental-care-recent">
          <div className="text-[10px] uppercase tracking-wide text-white/35 pt-2">Recent</div>
          {s.recent.slice(0, 8).map((r, i) => <Row key={`r${i}`} r={r} />)}
        </div>
      )}
    </div>
  );
}
